import { css, SerializedStyles } from '@emotion/react';
import React, { ReactNode } from 'react';

import { TOOLTIP_SECONDARY_TEXT, useTooltip } from '@/Client/Widgets';
import { FOCUSED_LINE_ITEM_TAB, FOCUSED_LINE_ITEM_TAB_NOTE } from './focusedTaskCardStyles';

/**
 * A line item row's tag (a flag, the instructions, a modifier), along the top of the row or at the start
 * of a strip, with a tooltip explaining it. Focusable, so the explanation is reachable without a pointer.
 */
export const FocusedLineItemTab = ({ variantStyle, explanationTitle, explanation, hint, testId, children }: {
  /** Layered onto FOCUSED_LINE_ITEM_TAB: the tab's kind (FOCUSED_LINE_ITEM_TAB_ERROR, …); omit for the ink tab. */
  variantStyle?: SerializedStyles;
  explanationTitle: string;
  explanation: ReactNode;
  /** How to change what the tab flags, faded under the explanation. */
  hint?: string;
  testId: string;
  children: ReactNode;
}) => {
  const explanationTooltip = useTooltip(
    <>
      {explanation}
      {hint && <p css={TOOLTIP_SECONDARY_TEXT}>{hint}</p>}
    </>,
    { title: explanationTitle },
  );
  return (
    <span css={[FOCUSED_LINE_ITEM_TAB, variantStyle]} tabIndex={0} data-testid={testId} {...explanationTooltip.anchorProps}>
      {children}
      {explanationTooltip.tooltip}
    </span>
  );
};

const INSTRUCTIONS_PARAGRAPH = css`
  label: INSTRUCTIONS_PARAGRAPH;
  display: block;

  & + & {
    margin-top: 6px;
  }
`;

/** Each run of whitespace holding a line break starts a new paragraph. */
const getInstructionsParagraphs = (instructions: string) => instructions.split(/\s*\n\s*/);

/** The Instructions tab: a line item template's description, read as instructions for the line, in full in its tooltip. */
export const FocusedLineItemInstructionsTab = ({ instructions }: { instructions: string }) => (
  <FocusedLineItemTab
    variantStyle={FOCUSED_LINE_ITEM_TAB_NOTE}
    testId='focused-line-item-instructions-tab'
    explanationTitle='Template Instructions'
    explanation={getInstructionsParagraphs(instructions).map((paragraph, index) => <span key={index} css={INSTRUCTIONS_PARAGRAPH}>{paragraph}</span>)}
  >
    <svg viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.6' aria-hidden>
      <path d='M3 3.5h10M3 8h10M3 12.5h6' />
    </svg>
    {'Instructions'}
  </FocusedLineItemTab>
);
